import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Download, ChevronDown, Check, ArrowUp, ArrowDown, Package } from "lucide-react";
import { FaArrowRight } from "react-icons/fa";
import "./Orders.css";
import { orderAPI, authAPI, analyticsAPI } from "../../api";
import ViewOrders from "./ViewOrders";
import AccessModule from "../../components/AccessModule/AccessModule";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Order {
  _id: string;
  customerName: string;
  totalAmount: string;
  orderStatus: string;
  createdAt: string;
  orderId: string;
  products?: any[];
}


const formatDateTime = (date: string) =>
  new Date(date)
    .toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(" at ", ", ")
    .toLowerCase();

const getLocalDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OrdersPage = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage]   = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery]   = useState(location.state?.orderId || location.state?.searchQuery || "");
  const [startDate, setStartDate]       = useState("");
  const [endDate, setEndDate]           = useState("");
  const [orderStatus, setStatus]        = useState(location.state?.status !== undefined ? location.state.status : "processing");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [orders, setOrders]             = useState<Order[]>([]);
  const [viewOrders, setViewOrders]     = useState<Order | null>(null);
  const [totalCount, setTotalCount]     = useState<number>(0);
  const [totalRevenue, setTotalRevenue] = useState<number | string>("–");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [downloadingCsvId, setDownloadingCsvId] = useState<string | null>(null);
  const [bulkExportingCsv, setBulkExportingCsv] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(true);

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);  

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );
  
        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );
  
        setVerificationStatus(
          sellerRes.data.verification_status
        );
  
        setIsBlocked(
          sellerRes.data.isBlock
        );
  
        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };
  
    checkAccess();
  }, []);

  // If navigation state changes, update search
  useEffect(() => {
    if (location.state?.orderId || location.state?.searchQuery) {
      setSearchQuery(location.state.orderId || location.state.searchQuery);
    }
    if (location.state?.status !== undefined) {
      setStatus(location.state.status);
    }
  }, [location.state]);

  const sellerData = JSON.parse(localStorage.getItem("seller"));
  const sellerId = sellerData?._id;

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const res = await orderAPI.getOrderList({
        startDate,
        endDate,
        orderStatus,
        searchQuery,
        currentPage,
        itemsPerPage,
        sellerId,
        sortBy,
        sortOrder
      });
      const { item, currentPage: page, totalCount , totalAmountSum } = res.data.data;
      setOrders(item || []);
      setCurrentPage(page);
      setTotalCount(totalCount);
      setTotalRevenue(totalAmountSum || 0);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    fetchOrders();
  }, [endDate, orderStatus, searchQuery, currentPage, itemsPerPage, sortBy, sortOrder, accessChecked, isBlocked, verificationStatus]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getOrderStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pickup":
        return "Pickup";
      case "pending":
      case "processing":
        return "Processing";
      case "cancelled":
        return "Cancelled";
      case "out_for_delivery":
        return "Out for Delivery";
      case "delivered":
        return "Delivered";
      default:
        return status;
    }
  };

  const [exporting, setExporting] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

  const handleDownloadSellerInvoice = async (order: Order) => {
    if (downloadingInvoiceId) return;
    setDownloadingInvoiceId(order._id);
    try {
      const res = await orderAPI.downloadSellerInvoice(order._id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Seller-Invoice-${order.orderId || order._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to generate seller invoice. Please try again.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleDownloadCsv = async (order: Order) => {
    if (downloadingCsvId) return;
    setDownloadingCsvId(order._id);
    try {
      const res = await orderAPI.downloadSettlementCsv(order._id);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Settlement_${order.orderId || order._id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to generate CSV. Please try again.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setDownloadingCsvId(null);
    }
  };

  const handleBulkSettlementExport = async () => {
    if (bulkExportingCsv) return;
    setBulkExportingCsv(true);
    try {
      const dataToExport = await fetchExportData();
      const deliveredOrders = dataToExport.filter((o: any) => o.orderStatus === 'delivered');
      if (deliveredOrders.length === 0) {
        Swal.fire({ title: 'No Data', text: 'No delivered orders found in this list to export.', icon: 'info', confirmButtonColor: '#10b981' });
        return;
      }
      const orderIds = deliveredOrders.map((o: any) => o._id);
      
      const res = await orderAPI.downloadBulkSettlementCsv({ orderIds });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bulk_Settlement_Report.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to generate Bulk CSV. Please try again.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setBulkExportingCsv(false);
    }
  };

  const fetchExportData = async () => {
    const res = await orderAPI.getOrderList({
      startDate,
      endDate,
      orderStatus,
      searchQuery,
      currentPage: 1,
      itemsPerPage: 99999,
      sellerId
    });
    return res.data.data.item || [];
  };

  const exportToPDF = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const doc = new jsPDF();

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(20, 184, 166);
      doc.text("DECSOPLUS STORE", 14, 15);

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Orders Report", 14, 21);

      const dateStr = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      doc.text(`Generated: ${dateStr}`, 196, 15, { align: 'right' });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(14, 25, 196, 25);

      doc.setFontSize(9);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Orders: ${dataToExport.length}`, 14, 31);

      const tableHeaders = [["S.No", "Order ID", "Customer", "Amount", "Status", "Date"]];
      const tableRows = dataToExport.map((order: any, index: number) => [
        (index + 1).toString(),
        order.orderId || '—',
        order.customerName || '—',
        `Rs. ${Number(order.totalAmount || 0).toFixed(0)}`,
        getOrderStatusLabel(order.orderStatus) || '—',
        order.createdAt ? formatDateTime(order.createdAt) : '—'
      ]);

      autoTable(doc, {
        startY: 35,
        head: tableHeaders,
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [20, 184, 166], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'left' },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85], valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data: any) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(8);
          doc.setFont("Helvetica", "normal");
          doc.setTextColor(148, 163, 184);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          doc.text("DecsoPlus © All Rights Reserved", doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
        },
        margin: { top: 35, bottom: 15, left: 14, right: 14 }
      });

      doc.save(`DecsoPlus_Orders_${new Date().toISOString().slice(0, 10)}.pdf`);
      Swal.fire({ title: 'Success!', text: 'Orders exported to PDF successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const headers = ["S.No", "Order ID", "Customer", "Amount", "Status", "Date"];
      const rows = dataToExport.map((order: any, index: number) => [
        index + 1,
        `"${(order.orderId || '').replace(/"/g, '""')}"`,
        `"${(order.customerName || '').replace(/"/g, '""')}"`,
        Number(order.totalAmount || 0).toFixed(0),
        `"${(getOrderStatusLabel(order.orderStatus) || '').replace(/"/g, '""')}"`,
        `"${order.createdAt ? formatDateTime(order.createdAt) : ''}"`
      ]);

      const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `DecsoPlus_Orders_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Swal.fire({ title: 'Success!', text: 'Orders exported to CSV successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = () => {
    Swal.fire({
      title: 'Export Format',
      text: 'Choose which format you want to export to:',
      icon: 'question',
      showCloseButton: true,
      showCancelButton: false,
      showDenyButton: true,
      confirmButtonText: 'Export as CSV',
      denyButtonText: 'Export as PDF',
      confirmButtonColor: '#10b981',
      denyButtonColor: '#f97316',
      width: '400px',
      customClass: {
        actions: 'my-actions',
        confirmButton: 'order-1',
        denyButton: 'order-2',
      }
    }).then((result) => {
      if (result.isConfirmed) {
        exportToCSV();
      } else if (result.isDenied) {
        exportToPDF();
      }
    });
  };

  const getPaginationGroup = () => {
    const pages: (number | string)[] = [];
    const maxPages = Number(totalPages) || 1;
    const currPage = Number(currentPage) || 1;
    if (maxPages <= 8) {
      for (let i = 1; i <= maxPages; i++) pages.push(i);
    } else {
      const lb = 2, rb = 2;
      let sp = Math.max(lb + 1, currPage - 1);
      let ep = Math.min(maxPages - rb, currPage + 1);
      if (currPage <= lb + 2) ep = lb + 3;
      if (currPage >= maxPages - rb - 1) sp = maxPages - rb - 2;
      for (let i = 1; i <= lb; i++) pages.push(i);
      if (sp > lb + 1) pages.push("...");
      for (let i = sp; i <= ep; i++) pages.push(i);
      if (ep < maxPages - rb) pages.push("...");
      for (let i = maxPages - rb + 1; i <= maxPages; i++) pages.push(i);
    }
    return pages;
  };

 
  
  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }

  if (viewOrders) {
    return <ViewOrders order={viewOrders} onBack={() => setViewOrders(null)} />;
  }

  return (
    <div className="ord-page fade-in">
      <div className="page-header ord-page-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div className="ord-page-header-text">
          <h1 className="page-title">Orders</h1>
          <p className="page-subtitle">Manage and track your customer orders</p>
        </div>
        <div className="ord-page-header-card" style={{ background: 'white', padding: '10px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: '18px' }}>₹</span>
          </div>
          <div>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', margin: 0 }}>
              {orderStatus === '' ? 'Total Revenue' : orderStatus === 'processing' ? 'Processing Amount' : orderStatus === 'delivered' ? 'Delivered Amount' : `${orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1).replace(/_/g, ' ')} Amount`}
            </p>
            <p style={{ fontSize: '18px', fontWeight: '800', color: '#38b92cff', margin: 0 }}>₹{typeof totalRevenue === 'number' ? totalRevenue.toLocaleString() : totalRevenue}</p>
          </div>
        </div>
      </div>

      <div className="ord-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 210px)', minHeight: '500px' }}>

        {/* ── Filter Bar ── */}
        <div className="ord-filter-bar" style={{ flexShrink: 0 }}>
          <div className="ord-filter-left">

            {/* Search */}
            <div className="ord-search-wrap">
              <Search className="ord-search-icon" size={16} />
              <input
                type="text"
                placeholder="Search customer, order ID and Amount…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.trimStart())}
                className="ord-search-input"
              />
            </div>

            {/* Date range */}
            <div className="ord-date-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569', minWidth: '70px' }}>From Date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="ord-date-input"
                max={endDate || getLocalDateString()}
              />
            </div>
            <div className="ord-date-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569', minWidth: '70px' }}>To Date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="ord-date-input"
                min={startDate}
                max={getLocalDateString()}
              />
            </div>

            {/* Responsive Order Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              
              {/* MOBILE DROPDOWN */}
              <div className="hide-on-desktop" style={{ position: 'relative', width: 'auto', minWidth: '160px' }}>
                <button 
                  onClick={() => setIsStatusOpen(!isStatusOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 16px', background: 'white', border: '1px solid #e2e8f0',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s',
                    color: '#334155', fontWeight: '600', gap: '8px', height: '100%', width: '100%'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      width: '8px', height: '8px', borderRadius: '50%', 
                      background: orderStatus === '' ? '#94a3b8' : orderStatus === 'processing' ? '#f59e0b' : orderStatus === 'pickup' ? '#3b82f6' : orderStatus === 'out_for_delivery' ? '#a855f7' : orderStatus === 'cancelled' ? '#64748b' : orderStatus === 'return_initiated' ? '#f59e0b' : orderStatus === 'return_completed' ? '#10b981' : '#10b981' 
                    }} />
                    {orderStatus === '' ? 'All Orders' : getOrderStatusLabel(orderStatus)}
                  </div>
                  <ChevronDown size={16} strokeWidth={2.5} style={{ 
                    transform: isStatusOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                    color: '#94a3b8' 
                  }} />
                </button>
                
                {isStatusOpen && (
                  <>
                    <div onClick={() => setIsStatusOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div className="fade-in" style={{
                      position: 'absolute', top: 'calc(100% + 8px)', right: 0, left: 0,
                      background: 'white', borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)',
                      border: '1px solid #f1f5f9', padding: '6px', zIndex: 50,
                    }}>
                      {[
                        { value: '', label: 'All Orders' },
                        { value: 'processing', label: 'Processing' },
                        { value: 'pickup', label: 'Pickup' },
                        { value: 'out_for_delivery', label: 'Out for Delivery' },
                        { value: 'delivered', label: 'Delivered' },
                        { value: 'cancelled', label: 'Cancelled' }
                      ].map(s => (
                        <button
                          key={s.value}
                          onClick={() => { setStatus(s.value); setIsStatusOpen(false); setCurrentPage(1); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'transparent',
                            cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                            color: orderStatus === s.value ? '#14b8a6' : '#475569',
                            fontWeight: orderStatus === s.value ? '700' : '500',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ 
                              width: '8px', height: '8px', borderRadius: '50%',
                              background: s.value === '' ? '#94a3b8' : s.value === 'processing' ? '#f59e0b' : s.value === 'pickup' ? '#3b82f6' : s.value === 'out_for_delivery' ? '#a855f7' : s.value === 'cancelled' ? '#64748b' : '#10b981' 
                            }} />
                            {s.label}
                          </div>
                          {orderStatus === s.value && <Check size={14} strokeWidth={3} />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* DESKTOP TABS */}
              <div className="ord-tabs-container hide-on-mobile" style={{ width: '100%' }}>
                {[
                  { value: '', label: 'All Orders' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'pickup', label: 'Pickup' },
                  { value: 'out_for_delivery', label: 'Out for Delivery' },
                  { value: 'delivered', label: 'Delivered' },
                  { value: 'cancelled', label: 'Cancelled' }
                ].map(s => (
                  <button
                    key={s.value}
                    onClick={() => {
                      setStatus(s.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap',
                      border: orderStatus === s.value ? 'none' : '1px solid #e2e8f0',
                      background: orderStatus === s.value 
                        ? (s.value === '' ? '#334155' : s.value === 'processing' ? '#f59e0b' : s.value === 'pickup' ? '#3b82f6' : s.value === 'out_for_delivery' ? '#a855f7' : s.value === 'cancelled' ? '#64748b' : '#10b981')
                        : 'white',
                      color: orderStatus === s.value ? 'white' : '#64748b',
                      fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: orderStatus === s.value ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {orderStatus === s.value && <Check size={14} strokeWidth={3} />}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

               {(searchQuery || startDate || endDate || orderStatus !== 'processing') && (
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSearchQuery("");
                    setStatus("processing");
                  }}
                  className="ord-btn-clear"
                >
                  Clear
                </button>
              )}
          </div>
           {orderStatus === 'delivered' && (
             <button 
               className="ord-btn-export" 
               onClick={handleBulkSettlementExport} 
               disabled={bulkExportingCsv}
               style={{ backgroundColor: '#ecfdf5', color: '#ecf1f0ff', border: '1px solid #a7f3d0', marginRight: '8px' }}
             >
               {bulkExportingCsv ? <span className="vo-btn-spinner" style={{ width: '14px', height: '14px', margin: 0 }}></span> : <Download size={14} />}
               {bulkExportingCsv ? 'Downloading...' : 'Settlement Report'}
             </button>
           )}
          {/* Export */}
          <button className="ord-btn-export" onClick={handleExportClick} disabled={exporting}>
            <Download size={14} />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>

        {/* ── Table ── */}
        <div className="ord-table-wrap products-table-container" style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', display: 'block' }}>
          {!loadingOrders && orders.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', width: '100%', background: '#fff', borderRadius: '12px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                <path d="M53.3333 18.6667H29.3333L24 13.3333H10.6667C7.72 13.3333 5.33333 15.72 5.33333 18.6667V45.3333C5.33333 48.28 7.72 50.6667 10.6667 50.6667H53.3333C56.28 50.6667 58.6667 48.28 58.6667 45.3333V24C58.6667 21.0533 56.28 18.6667 53.3333 18.6667Z" fill="url(#folder-grad)"/>
                <defs>
                  <linearGradient id="folder-grad" x1="5.33333" y1="13.3333" x2="58.6667" y2="50.6667" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FCD34D"/>
                    <stop offset="1" stopColor="#F59E0B"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', fontFamily: 'inherit' }}>
                No {orderStatus ? getOrderStatusLabel(orderStatus).toLowerCase() : ''} orders found
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', maxWidth: '400px', textAlign: 'center' }}>
                No records match the selected category, dates, or search term.
              </p>
              {(searchQuery || startDate || endDate || orderStatus !== '') && (
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSearchQuery("");
                    setStatus("");
                    setCurrentPage(1);
                  }}
                  style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <table className="ord-table" style={{ margin: 0, width: '100%', minWidth: '1350px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '16px 16px 16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '80px', whiteSpace: 'nowrap' }}>SL.NO</th>
                  <th className="ord-col-id" onClick={() => handleSort('orderId')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Order ID {sortBy === 'orderId' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                  </th>
                  <th className="ord-col-product" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap', minWidth: '400px' }}>
                    Product
                  </th>
                  <th className="ord-col-cust" onClick={() => handleSort('customerName')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', minWidth: '300px' }}>
                    Customer {sortBy === 'customerName' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                  </th>
                  <th className="ord-col-amt" onClick={() => handleSort('totalAmount')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', minWidth: '150px' }}>
                    Amount {sortBy === 'totalAmount' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                  </th>
                  <th className="ord-col-date" onClick={() => handleSort('createdAt')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Date {sortBy === 'createdAt' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                  </th>
                  <th className="ord-col-status" onClick={() => handleSort('orderStatus')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Order Status {sortBy === 'orderStatus' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                  </th>
                  <th className="ord-col-action" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingOrders ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '0', textAlign: 'center', color: '#64748b' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', minHeight: '400px', width: '100%' }}>
                        <div style={{ width: '36px', height: '36px', border: '3px solid #f1f5f9', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#475569' }}>Fetching latest orders...</span>
                      </div>
                    </td>
                  </tr>
                ) : orders.map((order, index) => (
                  <tr key={order._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '16px 16px 16px 24px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="ord-col-id" style={{ padding: '16px 24px' }}>
                      <span className="ord-id" title={order._id} style={{ fontWeight: '600', color: '#475569' }}>
                        {order.orderId}
                      </span>
                    </td>
                    <td className="ord-col-product" style={{ padding: '16px 24px' }}>
                      {(() => {
                        const p = order.products?.find((p: any) => p.seller_id === sellerId) || order.products?.[0] || {};
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {(() => {
                              const imgData = p.image || p.images || p.productImage;
                              const imgSrc = Array.isArray(imgData) 
                                ? (imgData[0]?.url || (typeof imgData[0] === 'string' ? imgData[0] : null))
                                : (typeof imgData === 'object' && imgData !== null ? imgData.url : imgData);
                              return (
                                <>
                                  {imgSrc && typeof imgSrc === 'string' && imgSrc !== '[object Object]' ? (
                                    <>
                                      <img 
                                        src={imgSrc} 
                                        alt={p.productName || 'Product'} 
                                        style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0' }} 
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                      <div style={{ display: 'none', width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                                        <Package size={22} color="#94a3b8" />
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                                      <Package size={22} color="#94a3b8" />
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div title={p.productName || 'Unknown Product'} style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.productName || 'Unknown Product'}</div>
                              {p.category && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.category}</div>}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="ord-col-cust" style={{ padding: '16px 24px' }}>
                      <div className="ord-customer" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="ord-avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#eff6ff', color: '#f1f2f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600' }}>
                          {order.customerName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <span className="ord-cust-name" style={{ fontWeight: '500', color: '#1e293b' }} title={order.customerName}>
                          {order.customerName?.length > 15 ? `${order.customerName.substring(0, 15)}...` : order.customerName}
                        </span>
                      </div>
                    </td>

                    <td className="ord-col-amt" style={{ padding: '16px 24px' }}>
                      <span className="ord-amount" style={{ fontWeight: '600', color: '#0f172a' }}> ₹{Number(order?.totalAmount || 0).toFixed(0)}</span>
                    </td>

                    <td className="ord-col-date" style={{ padding: '16px 24px' }}>
                      <span className="ord-date" style={{ color: '#64748b', fontSize: '14px' }}>{formatDateTime(order.createdAt)}</span>
                    </td>

                    <td className="ord-col-status" style={{ padding: '16px 24px' }}>
                      {(() => {
                        const s = (order.orderStatus || '').toLowerCase();
                        const badgeClass =
                          s === "processing" || s === "pending"
                            ? "ord-badge ord-badge-processing"
                            : s === "pickup"
                            ? "ord-badge ord-badge-pickup"
                            : s === "out_for_delivery"
                            ? "ord-badge ord-badge-out-for-delivery"
                            : s === "delivered"
                            ? "ord-badge ord-badge-delivered"
                            : s === "cancelled"
                            ? "ord-badge ord-badge-cancelled"
                            : "ord-badge ord-badge-default";

                        return (
                          <span
                            className={badgeClass}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            {getOrderStatusLabel(order.orderStatus)}
                          </span>
                        );
                      })()}
                    </td>

                    {/* ── Actions Column ── */}
                    <td className="ord-col-action" style={{ padding: '16px 24px' }}>
                      <div className="ord-action-group">
                        {order.orderStatus === 'delivered' && (
                          <button
                            className="ord-view-btn"
                            title="Download Settlement CSV"
                            onClick={() => handleDownloadCsv(order)}
                            disabled={downloadingCsvId === order._id}
                            style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#10b981', marginRight: '8px' }}
                          >
                            {downloadingCsvId === order._id ? <span className="vo-btn-spinner" style={{ width: '13px', height: '13px', margin: 0 }}></span> : <Download size={13} />}
                          </button>
                        )}
                        <button
                          className="ord-view-btn"
                          title="View Order"
                          onClick={() => setViewOrders(order)}
                          style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#2563eb' }}
                        >
                          <FaArrowRight size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer / Pagination ── */}
        <div className="ord-footer" style={{ flexShrink: 0 }}>
          <div className="ord-footer-left">
            <span className="ord-page-info">
              Showing{" "}
              {totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
              {" "}–{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
            </span>
            <div className="ord-per-page-wrap">
              <span>Per page:</span>
              <select
                className="ord-per-page-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="ord-pagination">
            <button
              className="ord-pg-btn"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={Number(currentPage) <= 1}
              title="Previous"
            >
              <ChevronLeft size={16} />
            </button>

            {getPaginationGroup().map((pg, i) =>
              pg === "..." ? (
                <span key={`ellipsis-${i}`} className="ord-pg-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={pg}
                  className={`ord-pg-btn${Number(currentPage) === Number(pg) ? " active" : ""}`}
                  onClick={() => typeof pg === "number" && setCurrentPage(pg)}
                >
                  {pg}
                </button>
              )
            )}

            <button
              className="ord-pg-btn"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages || 1))}
              disabled={Number(currentPage) >= (Number(totalPages) || 1)}
              title="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrdersPage;